import { ENV, materialConfig, newMaterialUrl } from '@/common/config';
import { loadLanguageAsync } from '@/locales';
import { isIn3D } from '@/utils';
import { insertScriptDynamically } from '@/utils/insertScriptDynamically';
import request from '@/utils/request';
import { history } from '@umijs/max';
import { sunvegaAPI } from 'utils';
import {
  isInIframe,
  postIframeLoadingStatus,
  postKeyDownToReact,
  postPreloadComplete,
} from './hooks';
import {
  changeLoginStatus,
  changeUserInfoStatus,
  isLogin,
} from './models/userInfo';
import { getUserInfo } from './service/login';
import { getUserSettingInfo, setUserSettingInfo } from './service/userInfo';
import { getValueFromSearchParams, modifyUrl } from './utils';
import { initSensor } from './utils/sevsors';
import './utils/warningFilter';
request.defaults.baseURL = materialConfig;

// 初始化三维家API
sunvegaAPI.init(`material-ui${isIn3D ? '-inner' : ''}`);

import { traversingElements } from '@/components/MaterialList/utils';
import { setHasInitialized } from '@/utils/appInit';
import { recordRouteHistory } from '@/utils/routeHistory';
import { initSearchBadcaseSwitch } from '@/utils/searchBadcase';
import { isVersionValid } from './constants';
import { isOversea } from './models/common';
request.defaults.baseURL = materialConfig;
const shouldRecord = window.location.href.includes('material-ui');
const sdVersion = getValueFromSearchParams('sdVersion');
const isNewVersion = isVersionValid(sdVersion);

// 标记是否已经初始化过（使用工具函数管理）
let hasInitialized = false;

const getOpenNewWindowUrl = () => {
  const urlObj = new URL(window.location.href);

  if (isInIframe && !urlObj.searchParams.has('librarySource')) {
    urlObj.searchParams.set('librarySource', '1');
  }

  return modifyUrl(urlObj.toString(), [
    'expandIframeWidth',
    'directoryMode',
    'searchImg',
  ]);
};

/**
 * @description 监听父窗口事件
 */
const onMaterialListener = (event: MessageEvent) => {
  // 修改语言
  // const lang = event?.data?.data?.lang
  // if (event?.data?.data?.type === 'changeLanguage' && lang) {
  //   loadLanguageAsync()
  //   replaceURLParam('lang', lang);
  // }
  const { origin, data } = event?.data ?? {};
  const { action, params } = data ?? {};
  // 检查消息来源和操作是否正确
  if (origin !== 'uiapi.materialUi' || !action) return;

  if (action === 'openNewWindow') {
    traversingElements();
    window?.parent?.postMessage(
      {
        origin: 'uiapi.materialUiplugin',
        data: {
          action: 'windowUrl',
          params: {
            url: getOpenNewWindowUrl(),
            mode: 'home',
            type: 'home',
          },
        },
      },
      '*',
    );
  }
  if (action === 'collapseOpenChange') {
    // 如果是以图搜图
    if (getValueFromSearchParams('searchImg')) {
      (globalThis as any)?.setIframePlaceholderExpand?.(false);
    }
    return;
  }
  // 切换模块
  if (action === 'pathChange') {
    const code = params;
    let newSearch = new URLSearchParams(window.location.search);
    history.push(`/tool/${code}?${newSearch}`);
  }

  if (action === 'setDirectoryMode' && params?.directoryMode) {
    const setDirectoryMode = (globalThis as any)._setDirectoryMode;
    if (setDirectoryMode && typeof setDirectoryMode === 'function') {
      setDirectoryMode(params.directoryMode);
    }
  }
};
/**
 * @description 通知父窗口点击事件
 */
const iframeContentFocusEvent = () => {
  window.parent.postMessage('iframeContentFocusEvent', '*');
};
/**
 * @description 禁止 ctrl+滚轮 缩放页面
 */
const onWheelScalePage = (event: WheelEvent) => {
  if (event.ctrlKey) {
    event.preventDefault();
  }
};
/**
 * @description 屏蔽鼠标右键功能
 */
const onContextMenu = (event: MouseEvent) => {
  event.preventDefault();
  event.stopPropagation();
  return false;
};
/**
 * @description 屏蔽鼠标右键按下事件
 */
const onMouseDown = (event: MouseEvent) => {
  if (event.button === 2) {
    // 右键按钮
    event.preventDefault();
    event.stopPropagation();
    return false;
  }
};
/**
 * @description 初始化应用
 * 抽离初始化逻辑，可以在多个地方调用
 */
async function initApplication() {
  postIframeLoadingStatus(false);
  postPreloadComplete();
  if (isOversea) {
    loadLanguageAsync();
  }
  const data: any = await getUserInfo();
  initSensor(data?.userId);
  void initSearchBadcaseSwitch();
  if (data) {
    changeLoginStatus(true);
    changeUserInfoStatus(data);
  }
  insertScriptDynamically(
    `https://3vj-fe.3vjia.com/vnpm/freeback/${ENV}/freeback.umd.js?t=${
      sdVersion || Date.now()
    }`,
  );
  insertScriptDynamically(
    `https://3vj-fe.3vjia.com/sso-plus-ui/${ENV}/js/ssoPlus.umd.min.js`,
  );
  const handleBeforeUnload = () => {
    window.removeEventListener('focus', iframeContentFocusEvent);
    window.removeEventListener('message', onMaterialListener);
    window.removeEventListener('wheel', onWheelScalePage);
    window.removeEventListener('contextmenu', onContextMenu, true);
    window.removeEventListener('mousedown', onMouseDown, true);
    document.removeEventListener('contextmenu', onContextMenu, true);
    document.removeEventListener('mousedown', onMouseDown, true);
  };
  // 设置监听
  if (isIn3D) {
    window.addEventListener('keydown', onKeydown, true);
    window.addEventListener('wheel', onWheelScalePage, { passive: false });
    window.addEventListener('focus', iframeContentFocusEvent);
    window.addEventListener('message', onMaterialListener);
    // 使用捕获阶段来更早地拦截右键事件，同时在 window 和 document 级别都添加
    window.addEventListener('contextmenu', onContextMenu, true);
    window.addEventListener('mousedown', onMouseDown, true);
    document.addEventListener('contextmenu', onContextMenu, true);
    document.addEventListener('mousedown', onMouseDown, true);
    window.addEventListener('beforeunload', handleBeforeUnload);
  }
  hasInitialized = true;
  setHasInitialized(true);
}
/**
 * @description 改变文本值
 */
const onKeydown = async (e: any) => {
  if (document?.activeElement?.tagName === 'INPUT') return;
  const inputValue = e.target.value;
  const pattern = /[^\u0000-\u00FF]/; // 使用正则表达式匹配非中文字符
  if (pattern.test(inputValue)) {
    e.preventDefault(); // 阻止输入中文字符
  }
  const excludeHotKeyList = [
    'CapsLock',
    'Space',
    'NumLock',
    'Escape',
    'Tab',
    'Delete',
    'Backspace',
    'MetaLeft',
    'ContextMenu',
  ];
  if (excludeHotKeyList.includes(e.code) || e.code.length == 0) return;
  let content = '';
  const code =
    e.key == 'Process' ? inputValue.toLowerCase() : e.key.toLowerCase();
  if (
    code.indexOf('shift') == -1 &&
    code.indexOf('control') == -1 &&
    code.indexOf('alt') == -1
  ) {
    content = e.shiftKey ? 'Shift+' : '';
    content += e.ctrlKey ? 'Ctrl+' : '';
    content += e.altKey ? 'Alt+' : '';
    content +=
      e.key == 'Process' ? inputValue.toUpperCase() : e.key.toUpperCase();
  }
  if (content == '' || content == undefined) return;
  postKeyDownToReact(content);
};
export async function getInitialState(): Promise<{ name: string }> {
  // 如果是空白页面路由，跳过初始化逻辑，避免 API 请求阻塞
  if (window.location.pathname.includes('/blank')) {
    return { name: '@umijs/max' };
  }

  // 执行初始化
  await checkRedirect();
  await initApplication();
  return { name: '@umijs/max' };
}
/**
 * @description 通过接口判断是否要重定向
 */
const checkRedirect = async () => {
  // 不在3d里面，并且是通过window.opener打开的 就重定向到此地址
  // console.log(!isIn3D, window.opener, shouldRecord, '-----------');
  if (
    !isIn3D &&
    window.opener &&
    (shouldRecord || isNewVersion) &&
    !getValueFromSearchParams('isRecord')
  ) {
    if (!isLogin) return;
    const userInfo = await getUserSettingInfo();
    const material_home_url = userInfo?.material_home_url;
    // console.log(material_home_url, 'material_home_url');
    if (material_home_url) {
      window.location.href = material_home_url;
    }
  }
};
export async function onRouteChange({ clientRoutes, location }: any) {
  // 如果从空白页面跳转过来，且还没有初始化，只做标记，不执行初始化
  // 让 Layouts 组件通过 refresh 方法来触发初始化，这样可以重新触发 getInitialState
  if (!hasInitialized && !location.pathname.includes('/blank')) {
    // 不在这里执行初始化，让 Layouts 组件来触发
    // 这里只处理 checkRedirect，因为它可能影响路由跳转
    await checkRedirect();
  }

  // 同步状态到工具函数
  if (hasInitialized) {
    setHasInitialized(true);
  }

  recordRouteHistory(location);

  if (isIn3D && (shouldRecord || isNewVersion)) {
    if (!isLogin) return;
    let material_home_url = (
      newMaterialUrl +
      location.pathname +
      location.search
    ).replace('/material-ui', '');
    if (isNewVersion) {
      material_home_url = window.location.href + '&isRecord=true';
    }
    // console.log('写入信息', material_home_url);
    setUserSettingInfo({ material_home_url });
  }
  if (
    location.pathname === '/material-ui' &&
    location.search === '?website' &&
    window.location.hostname.indexOf('aihouse') > -1
  ) {
    // 官网首页
    history.replace(`/?website`);
  } else if (location.pathname === '/material-ui') {
    // 旧的地址
    // console.log('onRouteChange12', `${newMaterialUrl}/tool${location.search}`);
    // // history.replace(`${newMaterialUrl}/tool${location.search}`);
    // window.location.href = `${newMaterialUrl}/tool${location.search}`
  }
}
